import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { Icon, type IconName } from './Icon'
import { IconButton } from './Button'
import './ui.css'

/* ------------------------------ Chip / Avatar ---------------------------- */

export function Chip({
  children,
  tone,
  icon,
  ai,
  onRemove,
  onClick,
  className,
}: {
  children: ReactNode
  tone?: 'accent' | 'moss' | 'danger' | 'glass' | 'on-photo'
  icon?: IconName
  /** Dashed border marks a value the AI suggested and a human has not confirmed. */
  ai?: boolean
  onRemove?: () => void
  onClick?: () => void
  className?: string
}) {
  const cls = clsx('chip', tone && 'chip--' + tone, ai && 'chip--ai', className)
  const content = (
    <>
      {ai && <Icon name="sparkle" size={11} strokeWidth={1.6} />}
      {icon && !ai && <Icon name={icon} size={13} />}
      {children}
      {onRemove && (
        <span
          className="chip__remove"
          role="button"
          tabIndex={0}
          aria-label="Quitar"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onRemove()
            }
          }}
        >
          <Icon name="close" size={12} strokeWidth={2} />
        </span>
      )}
    </>
  )
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick}>
        {content}
      </button>
    )
  }
  return <span className={cls}>{content}</span>
}

export function Avatar({
  src,
  fallback,
  size = 'md',
  ring,
  alt,
}: {
  src?: string | null
  fallback: string
  size?: 'sm' | 'md' | 'lg'
  ring?: boolean
  alt?: string
}) {
  return (
    <span className={clsx('avatar', size !== 'md' && 'avatar--' + size)}>
      {src ? <img src={src} alt={alt ?? ''} loading="lazy" decoding="async" /> : fallback}
      {ring && <span className="avatar__ring" />}
    </span>
  )
}

/* --------------------------------- Sheet -------------------------------- */

/**
 * Bottom sheet. Closes on backdrop tap and on Escape, restores focus, and locks
 * background scroll while open.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  action,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  action?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  // onClose lives in a ref so the effect below depends ONLY on `open`.
  // With `onClose` as a dependency, every keystroke inside the sheet
  // (parent re-render → new arrow identity) re-ran the effect and
  // ref.current?.focus() stole the focus from the input mid-word —
  // the keyboard collapsed on each letter. (§bug 2026-09-05)
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    ref.current?.focus()
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!open) return null
  // Rendered at the body, never inside the page: .page creates a stacking
  // context (z-index: 2), and a fixed sheet trapped inside it would paint
  // BELOW the dock — covering the sheet's own buttons. (§bug 2026-08-23)
  return createPortal(
    <div
      className="sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <span className="sheet__grip" />
        {(title || action) && (
          <div className="sheet__head">
            {title && <h2 className="sheet__title">{title}</h2>}
            <span className="spacer" />
            {action}
            <IconButton name="close" label="Cerrar" onClick={onClose} size={20} />
          </div>
        )}
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

/* -------------------------------- Dialog -------------------------------- */

export function Dialog({
  open,
  onClose,
  title,
  children,
  actions,
}: {
  open: boolean
  onClose: () => void
  title: string
  children?: ReactNode
  actions: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return createPortal(
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="dialog" role="alertdialog" aria-modal="true" aria-label={title}>
        <h2 className="dialog__title">{title}</h2>
        {children && <div className="dialog__body">{children}</div>}
        <div className="dialog__actions">{actions}</div>
      </div>
    </div>,
    document.body,
  )
}
